import { call, put, select, takeLatest } from 'redux-saga/effects';
import {
  VocabularyTestBlock,
  VocabularyTestHistory,
  VocabularyTestHistoryPage,
  StartVocabularyTestRequest,
  VocabularyTestTopic,
} from 'app/types';
import { axiosInstance } from 'setup/axiosInstance';
import vocabularyTestSlice, {
  vocabularyTestActions,
} from 'pages/VocabularyTest/store/vocabularyTest.slice';
import { RootState } from 'app/store';

function* fetchVocabularyTestHistory(page = 0, size = 5) {
  try {
    const { data }: { data: VocabularyTestHistoryPage } = yield axiosInstance(
      `/test-vocabulary/history/page`,
      {
        params: {
          page,
          size,
        },
      },
    );
    yield put(vocabularyTestActions.vocabularyTestHistorySet(data));
    // Note: callers that need the IN_PROGRESS test's block payload (currently
    // only the runner page) dispatch `vocabularyTestInProgressGet` explicitly.
    // Auto-triggering here used to fire a redundant POST /current on every
    // history fetch — once during startTesting (which already POSTed) and
    // again during the runner mount that follows.
  } catch (e) {
    console.error(e);
  }
}

function* vocabularyTestHistoryGetSaga({
  payload,
}: ReturnType<typeof vocabularyTestActions.vocabularyTestHistoryGet>) {
  yield call(
    fetchVocabularyTestHistory,
    payload?.page ?? 0,
    payload?.size ?? 5,
  );
}

function* vocabularyTestResultGetSaga({
  payload,
}: ReturnType<typeof vocabularyTestActions.vocabularyTestResultGet>) {
  try {
    const { data }: { data: VocabularyTestHistory } = yield axiosInstance(
      `/test-vocabulary/history/result/${payload}`,
    );
    yield put(vocabularyTestActions.vocabularyTestResultSet(data));
  } catch (e) {
    console.error(e);
    yield put(vocabularyTestActions.vocabularyTestResultFail());
  }
}

function* fetchVocabularyTestTopics() {
  try {
    const { data }: { data: VocabularyTestTopic[] } = yield axiosInstance.get(
      `/test-vocabulary/topics`,
    );
    yield put(vocabularyTestActions.vocabularyTestTopicsSet(data));
  } catch (e) {
    console.error(e);
    yield put(vocabularyTestActions.vocabularyTestTopicsFail());
  }
}

function* fetchVocabularyTestInProgress(request?: StartVocabularyTestRequest) {
  try {
    const { data }: { data: VocabularyTestBlock[] } = yield axiosInstance.post(
      `/test-vocabulary/current`,
      request,
    );
    yield put(vocabularyTestActions.vocabularyTestInProgressSet(data));
    yield call(fetchVocabularyTestTopics);
  } catch (e) {
    console.error(e);
    yield put(vocabularyTestActions.vocabularyTestTopicsFail());
  }
}

function* vocabularyTestInProgressGetSaga() {
  yield call(fetchVocabularyTestInProgress);
}

function* saveAndGetNextSaga({
  payload,
}: ReturnType<typeof vocabularyTestActions.saveAndGetNext>) {
  try {
    const activeTestId: string = yield select((rootState: RootState) =>
      vocabularyTestSlice.getSelectors().activeTestId(rootState.vocabularyTest),
    );
    const topics: VocabularyTestTopic[] = yield select(
      (rootState: RootState) =>
        rootState.vocabularyTest.vocabularyTestTopics.data,
    );

    if (!topics.some(({ status }) => status === 'UPCOMING')) {
      yield put(vocabularyTestActions.vocabularyTestInProgressSet(payload));
      yield put(vocabularyTestActions.vocabularyTestTopicsSet(topics));
      return;
    }

    const res: { data: VocabularyTestBlock[] } = yield axiosInstance.post(
      `/test-vocabulary/${activeTestId}/save`,
      payload,
    );

    yield put(vocabularyTestActions.vocabularyTestInProgressSet(res.data));
    yield call(fetchVocabularyTestTopics);
  } catch (e) {
    console.error(e);
    yield put(vocabularyTestActions.vocabularyTestTopicsFail());
  }
}

function* getPreviousWordsSaga() {
  try {
    const activeTestId: string = yield select((rootState: RootState) =>
      vocabularyTestSlice.getSelectors().activeTestId(rootState.vocabularyTest),
    );

    const res: { data: VocabularyTestBlock[] } = yield axiosInstance.post(
      `/test-vocabulary/${activeTestId}/previous`,
    );

    yield put(vocabularyTestActions.vocabularyTestInProgressSet(res.data));
    yield call(fetchVocabularyTestTopics);
  } catch (e) {
    console.error(e);
    yield put(vocabularyTestActions.vocabularyTestTopicsFail());
  }
}

function* startTestingSaga({
  payload,
}: ReturnType<typeof vocabularyTestActions.startTesting>) {
  try {
    yield call(fetchVocabularyTestInProgress, payload);
    yield call(fetchVocabularyTestHistory);
  } catch (e) {
    console.error(e);
  }
}

function* finishTestSaga({
  payload,
}: ReturnType<typeof vocabularyTestActions.finishTest>) {
  try {
    const activeTestId: string = yield select((rootState: RootState) =>
      vocabularyTestSlice.getSelectors().activeTestId(rootState.vocabularyTest),
    );

    const { data }: { data: VocabularyTestHistory } = yield axiosInstance.post(
      `/test-vocabulary/${activeTestId}/complete`,
      payload,
    );

    yield put(vocabularyTestActions.completedTestResultSet(data));
    yield call(fetchVocabularyTestHistory);
  } catch (e) {
    console.error(e);
    yield put(vocabularyTestActions.finishTestFail());
  }
}

function* addLearnedWordsToVocabularySaga({
  payload,
}: ReturnType<typeof vocabularyTestActions.addLearnedWordsToVocabulary>) {
  try {
    yield axiosInstance.post(`/test-vocabulary/add-learned-words/${payload}`);
    yield put(vocabularyTestActions.addLearnedWordsToVocabularySuccess());
  } catch (e) {
    console.error(e);
    yield put(vocabularyTestActions.addLearnedWordsToVocabularyFail());
  }
}

function* deleteTestHistorySaga({
  payload,
}: ReturnType<typeof vocabularyTestActions.deleteTestHistory>) {
  try {
    yield axiosInstance.delete(`/test-vocabulary/history/${payload}`);
    const {
      data,
      number,
      size,
    }: { data: VocabularyTestHistory[]; number: number; size: number } =
      yield select(
        (rootState: RootState) =>
          rootState.vocabularyTest.vocabularyTestHistory,
      );
    const nextPage = data.length === 1 && number > 0 ? number - 1 : number;
    yield call(fetchVocabularyTestHistory, nextPage, size);
    yield put(vocabularyTestActions.deleteTestHistorySuccess());
  } catch (e) {
    console.error(e);
  }
}

export default function* vocabularyTest() {
  yield takeLatest(
    vocabularyTestActions.vocabularyTestHistoryGet,
    vocabularyTestHistoryGetSaga,
  );
  yield takeLatest(
    vocabularyTestActions.vocabularyTestInProgressGet,
    vocabularyTestInProgressGetSaga,
  );
  yield takeLatest(
    vocabularyTestActions.vocabularyTestResultGet,
    vocabularyTestResultGetSaga,
  );
  yield takeLatest(vocabularyTestActions.saveAndGetNext, saveAndGetNextSaga);
  yield takeLatest(
    vocabularyTestActions.getPreviousWords,
    getPreviousWordsSaga,
  );
  yield takeLatest(vocabularyTestActions.startTesting, startTestingSaga);
  yield takeLatest(vocabularyTestActions.finishTest, finishTestSaga);
  yield takeLatest(
    vocabularyTestActions.addLearnedWordsToVocabulary,
    addLearnedWordsToVocabularySaga,
  );
  yield takeLatest(
    vocabularyTestActions.deleteTestHistory,
    deleteTestHistorySaga,
  );
}
